import { useCallback, useMemo } from "react"
import type { FC } from "react"

import ChangeHistoryTable from "./ChangeHistoryTable"
import CommissionRateForm from "./CommissionRateForm"
import styles from "./FeeRateTab.module.scss"

import {
	useSouhlAdminMutation,
	useSouhlAdminQuery,
} from "@/provider/apollo/Apollo"
import { graphql } from "@/souhl_admin/gql/__generated__"
import type { CommissionRateFormData } from "@/souhl_admin/utils/forms/commissionRate"

const GetCompanyCommissionRates = graphql(/* GraphQL */ `
	query GetCompanyCommissionRates($param: GetCompanyCommissionRatesParam!) {
		getCompanyCommissionRates(param: $param) {
			companyCommissionRates {
				companyId
				cancelFeeCommissionRate
				existingCustomerRereservationCommissionRate
				newCustomerRereservationCommissionRate
			}
		}
	}
`)

const GetCompanyCommissionRateChangeHistories = graphql(/* GraphQL */ `
	query GetCompanyCommissionRateChangeHistories(
		$param: GetCompanyCommissionRateChangeHistoriesParam!
	) {
		getCompanyCommissionRateChangeHistories(param: $param) {
			histories {
				...ChangeHistoryFields
			}
		}
	}
`)

const UpdateCompanyCommissionRates = graphql(/* GraphQL */ `
	mutation UpdateCompanyCommissionRates(
		$input: UpdateCompanyCommissionRatesInput!
	) {
		updateCompanyCommissionRates(input: $input) {
			companyCommissionRates {
				companyId
				cancelFeeCommissionRate
				existingCustomerRereservationCommissionRate
				newCustomerRereservationCommissionRate
			}
		}
	}
`)

type FeeRateTabProps = {
	companyId: string
}

const FeeRateTab: FC<FeeRateTabProps> = ({ companyId }) => {
	const { data: ratesData, refetch: refetchRates } = useSouhlAdminQuery(
		GetCompanyCommissionRates,
		{
			variables: { param: { companyId } },
			fetchPolicy: "no-cache",
		}
	)

	const { data: historiesData, refetch: refetchHistories } = useSouhlAdminQuery(
		GetCompanyCommissionRateChangeHistories,
		{
			variables: { param: { companyId } },
			fetchPolicy: "no-cache",
		}
	)

	const [updateRates, { loading: isUpdating }] = useSouhlAdminMutation(
		UpdateCompanyCommissionRates
	)

	const defaultValues: CommissionRateFormData = useMemo(() => {
		const rates = ratesData?.getCompanyCommissionRates?.companyCommissionRates
		const existingRate = rates?.existingCustomerRereservationCommissionRate ?? 0
		const newRate = rates?.newCustomerRereservationCommissionRate ?? 0
		return {
			cancelFeeCommissionRate: rates?.cancelFeeCommissionRate ?? 0,
			existingCustomerRereservationCommissionRate: existingRate,
			newCustomerRereservationCommissionRate: newRate,
			// 既存客用と新規客用が異なる会社は分離設定 ON で初期表示する
			isSplitNewCustomerRate: existingRate !== newRate,
		}
	}, [ratesData])

	const histories = useMemo(
		() =>
			historiesData?.getCompanyCommissionRateChangeHistories?.histories ?? [],
		[historiesData]
	)

	const handleSubmit = useCallback(
		async (data: CommissionRateFormData) => {
			await updateRates({
				variables: {
					input: {
						companyId,
						cancelFeeCommissionRate: data.cancelFeeCommissionRate,
						existingCustomerRereservationCommissionRate:
							data.existingCustomerRereservationCommissionRate,
						// 分離 OFF のときは新規顧客用に既存顧客用と同じ値を送る
						newCustomerRereservationCommissionRate:
							data.newCustomerRereservationCommissionRate ??
							data.existingCustomerRereservationCommissionRate,
					},
				},
			})
			await Promise.all([refetchRates(), refetchHistories()])
		},
		[companyId, updateRates, refetchRates, refetchHistories]
	)

	return (
		<div className={styles.fee_rate_tab}>
			<section className={styles.section}>
				<h2 className={styles.section_title}>手数料率設定</h2>
				<p className={styles.section_description}>
					この会社の手数料率を設定します。
					<br />
					変更は次回の相殺明細作成時から適用されます。
				</p>

				<div className={styles.current_rates}>
					<span className={styles.current_rate_item}>
						現在のキャンセル料回収手数料率：
						<strong>{defaultValues.cancelFeeCommissionRate}%</strong>
					</span>
					{defaultValues.isSplitNewCustomerRate ? (
						<>
							<span className={styles.current_rate_item}>
								現在の再予約手数料率（既存顧客用）：
								<strong>
									{defaultValues.existingCustomerRereservationCommissionRate}%
								</strong>
							</span>
							<span className={styles.current_rate_item}>
								現在の再予約手数料率（新規顧客用）：
								<strong>
									{defaultValues.newCustomerRereservationCommissionRate}%
								</strong>
							</span>
						</>
					) : (
						<span className={styles.current_rate_item}>
							現在の再予約手数料率：
							<strong>
								{defaultValues.existingCustomerRereservationCommissionRate}%
							</strong>
						</span>
					)}
				</div>

				<CommissionRateForm
					defaultValues={defaultValues}
					onSubmit={handleSubmit}
					isSubmitting={isUpdating}
				/>
			</section>

			<section className={styles.section}>
				<h2 className={styles.section_title}>変更履歴</h2>
				<ChangeHistoryTable histories={histories} />
			</section>
		</div>
	)
}

export default FeeRateTab
